import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { Subject, Observable, of } from 'rxjs';
import {
  TSkyUiVisibilityState,
  ISkyUiTooltipOptions,
  TSkyUiTooltipPosition,
  ISkyUiTooltipComponent,
} from '../../interface';
import { tooltipVisibilityAnimation } from './tooltip-animation';
import { delay, filter, switchMap } from 'rxjs/operators';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { Store } from '@skyeng/libs/shared/store';

interface TooltipStoreEvents {
  visibilityWillChange: VisibilityChangeEvent;
}

interface VisibilityChangeEvent {
  value: TSkyUiVisibilityState;
  delay: number;
}

const DEFAULT_SHOW_DELAY_MS = 0;
const DEFAULT_HIDE_DELAY_MS = 0;
const TOOLTIP_INITIAL_STATE = {
  visibilityValue: 'initial' as TSkyUiVisibilityState,
  preventHiding: false,
};

@DecorateUntilDestroy()
@Component({
  selector: 'sky-ui-tooltip',
  templateUrl: 'tooltip.html',
  styleUrls: ['tooltip.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [tooltipVisibilityAnimation],
})
export class SkyUiTooltipComponent implements ISkyUiTooltipComponent, OnInit, OnDestroy {
  private store = new Store<typeof TOOLTIP_INITIAL_STATE, TooltipStoreEvents>(TOOLTIP_INITIAL_STATE);
  private hidden = new Subject<void>();
  private visible = new Subject<void>();
  private preventHiding = false;

  hidden$: Observable<void> = this.hidden.asObservable();
  visible$: Observable<void> = this.visible.asObservable();
  options: ISkyUiTooltipOptions = {};
  position: TSkyUiTooltipPosition;
  visibilityValue$ = this.store.select('visibilityValue');

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initVisibilityWillChangeObserver();
  }

  ngOnDestroy(): void {}

  show(delay = DEFAULT_SHOW_DELAY_MS): void {
    this.setVisibility('visible', delay);
  }

  hide(delay = DEFAULT_HIDE_DELAY_MS): void {
    this.setVisibility('hidden', delay);
  }

  onMouseEnter(): void {
    if (this.options.manual || this.options.ignoreMouseEnter) {
      return;
    }

    this.setPreventHiding(true);
  }

  onMouseLeave(): void {
    if (this.options.manual) {
      return;
    }

    this.setPreventHiding(false);
    this.hide(this.options.hideDelay);
  }

  onVisibilityAnimationDone(): void {
    if (this.store.get('visibilityValue') === 'hidden') {
      this.hidden.next();
    } else {
      this.visible.next();
    }
  }

  setPreventHiding(preventHiding: boolean): void {
    this.preventHiding = preventHiding;
  }

  setOptions(options: ISkyUiTooltipOptions): void {
    this.options = options;

    this.changeDetectorRef.markForCheck();
  }

  setPosition(position: TSkyUiTooltipPosition): void {
    if (position === this.position) {
      return;
    }

    this.position = position;

    this.changeDetectorRef.markForCheck();
  }

  getShowAnimationTransform(): string {
    if (this.position === 'above') {
      return 'translateY(4px)';
    } else if (this.position === 'below') {
      return 'translateY(-4px)';
    } else if (this.position === 'after') {
      return 'translateX(-4px)';
    } else {
      return 'translateX(4px)';
    }
  }

  getHideAnimationTransform(): string {
    if (this.position === 'above') {
      return 'translateY(-4px)';
    } else if (this.position === 'below') {
      return 'translateY(4px)';
    } else if (this.position === 'after') {
      return 'translateX(4px)';
    } else {
      return 'translateX(-4px)';
    }
  }

  private initVisibilityWillChangeObserver(): void {
    this.store
      .on('visibilityWillChange')
      .pipe(
        switchMap(data => of(data.value).pipe(delay(data.delay))),
        filter(visibilityValue => !(visibilityValue === 'hidden' && this.preventHiding)),
        takeUntilDestroyed(this),
      )
      .subscribe(visibilityValue => this.store.updateField('visibilityValue', visibilityValue));
  }

  private setVisibility(value: TSkyUiVisibilityState, delay = 0): void {
    this.store.fire('visibilityWillChange', { value, delay });
  }
}
